import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";
import { getColor } from "../../brandConfig";

/**
 * Club52 Module Config — poker game organiser on the Block52 chain.
 *
 * SINGLE SOURCE OF TRUTH for permissions is the backend (club52/permissions.ts),
 * fetched via `modulePermissions`. Routes/nav use `permissionLevel` resolved at runtime.
 *
 * Routes:
 *   /club52                    dashboard (authed) — stats, upcoming games, create wizard
 *   /club52/players            player roster (authed)
 *   /club52/settings           module settings (authed, admin circle via "delete" level)
 *   /club52/game/:room/manage  game management (authed) — invitees/RSVPs, messages
 *   /club52/:room              PUBLIC join page — players join in-browser, no login
 */

const Club52Dashboard = React.lazy(() => import("./Club52Dashboard"));
const Club52Room = React.lazy(() => import("./Club52Room"));
const Club52GameManage = React.lazy(() => import("./Club52GameManage"));
const Club52Players = React.lazy(() => import("./Club52Players"));
const Club52Settings = React.lazy(() => import("./Club52Settings"));
const Club52Earnings = React.lazy(() => import("./Club52Earnings"));

const club52ModuleConfig: ModuleConfig = {
  id: "club52",
  name: "Club52",
  description:
    "Organise poker game nights on the Block52 chain — invites with RSVP + reminders, a private video room, player funding, tables and house earnings.",
  version: "0.1.0",
  enabled: true,
  category: "communication",
  order: 12,
  icon: "🎲",
  color: getColor("primaryBlue"),

  navigation: [
    {
      label: "Club52",
      path: "/club52",
      icon: "🎲",
      permissionLevel: "view",
    },
    {
      label: "Players",
      path: "/club52/players",
      icon: "🃏",
      permissionLevel: "view",
    },
    {
      label: "Earnings",
      path: "/club52/earnings",
      icon: "💰",
      permissionLevel: "view",
    },
    {
      label: "Club52 Settings",
      path: "/club52/settings",
      // "delete" resolves to the CLUB52_ADMIN + ADMIN circle (backend manifest) —
      // the treasury/settings surface must not show for plain CLUB52_USERs.
      permissionLevel: "delete",
      icon: "⚙️",
    },
  ],

  // Permissions resolved from backend at runtime via permissionLevel.
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  routes: [
    {
      path: "/club52",
      component: Club52Dashboard,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      // AUTHED — MUST come before the public /club52/:room catch-all so
      // "players"/"settings" aren't treated as room slugs.
      path: "/club52/players",
      component: Club52Players,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/club52/settings",
      component: Club52Settings,
      permissionLevel: "delete",
      useLayout: true,
    },
    {
      path: "/club52/earnings",
      component: Club52Earnings,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/club52/game/:room/manage",
      component: Club52GameManage,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      // PUBLIC — players join via the shared/personalised link with no auth.
      path: "/club52/:room",
      component: Club52Room,
      public: true,
      useLayout: false,
    },
  ],
};

export default club52ModuleConfig;
